'use client';

import { useCallback, useState, useRef } from 'react';
import Icon, { IconId } from '@/components/shared/Icons';
import dynamic from 'next/dynamic';
import useScrollEvent from '@/hooks/useScrollEvent';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });

interface Props {
  iconName: string;
  iconLabel: string;
  popperContent: string;
}

export default function IconWithPopper({ iconName, iconLabel, popperContent }: Props): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const onDismiss = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onMouseEvent = useCallback(
    async e => {
      e.preventDefault();
      e.stopPropagation();
      if (window !== undefined && e.type === 'mouseenter') {
        window.addEventListener('scroll', onDismiss, false);
      }
      setIsOpen(!isOpen);
    },
    [isOpen, onDismiss]
  );
  const touchHandler = useCallback(
    async e => {
      if (e.pointerType === 'touch') {
        if (window !== undefined) {
          window.addEventListener('scroll', onDismiss, false);
        }
        setIsOpen(!isOpen);
      }
      e.stopPropagation();
      e.preventDefault();
    },
    [isOpen, onDismiss]
  );
  useScrollEvent(isOpen, onDismiss);
  return (
    <>
      <div
        className="group flex gap-1"
        ref={triggerRef}
        onClick={touchHandler}
        onMouseEnter={onMouseEvent}
        onMouseLeave={onDismiss}
        role="presentation"
      >
        <Icon id={iconName as IconId} className="h-4 w-4 fill-neutral-60 group-hover:fill-brand-60" />
        <span className="text-b-xs group-hover:text-brand-60">{iconLabel}</span>
      </div>
      {isOpen && (
        <Popper
          triggerRef={triggerRef}
          isOpen={isOpen}
          placement="top-start"
          arrowSize="small"
          arrowStyles={{
            borderColor: '#4e545b'
          }}
        >
          <div className="h-max w-max overflow-hidden rounded-sm bg-neutral-70 p-2 text-b-xs/tight text-background">
            {popperContent}
          </div>
        </Popper>
      )}
    </>
  );
}
